import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Save, X, ChevronUp, ChevronDown, Check, HelpCircle } from "lucide-react";
import CourseManagerHelpModal from "./CourseManagerHelpModal";

const TESTIDS = {
  courseEditor: "course-editor",
  courseEditorBack: "course-editor-back",
  courseListItem: "course-list-item",
  courseNewBtn: "course-new-btn",
  courseHelpBtn: "course-help-btn",
  courseCreateSubmit: "course-create-submit",
  courseSaveMeta: "course-save-meta",
  courseDelete: "course-delete",
  lessonNew: "lesson-new-btn",
  lessonEdit: "lesson-edit-btn",
  lessonDelete: "lesson-delete-btn",
  lessonSave: "lesson-save-btn",
  quizAddQuestion: "quiz-add-question-btn",
  quizSaveAll: "quiz-save-all-btn",
  flashcardAdd: "flashcard-add-btn",
  flashcardSaveAll: "flashcard-save-all-btn",
};

// -----------------------------------------------------------------------------
// Course editor: metadata + lessons + quiz + flashcards
// -----------------------------------------------------------------------------
export function CourseEditor({ slug, onClose }) {
  const [course, setCourse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("meta"); // meta | lessons | quiz | flashcards
  const [editingLesson, setEditingLesson] = useState(null); // {order?, title, summary, content}
  const [quizDraft, setQuizDraft] = useState(null);
  const [cardsDraft, setCardsDraft] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/courses/${slug}`);
      setCourse(data);
      setQuizDraft(null);
      setCardsDraft(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load course");
    }
  };
  useEffect(() => { load(); }, [slug]);

  if (!course) {
    return (
      <div className="mt-6 bg-white border border-brand-linen rounded-3xl shadow-card p-6" data-testid={TESTIDS.courseEditor}>
        <div className="text-brand-muted">Loading course…</div>
      </div>
    );
  }

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/courses/${slug}`, {
        title: course.title,
        subtitle: course.subtitle || "",
        description: course.description || "",
        level: course.level || "Foundation",
        duration: course.duration || "",
        thumbnail: course.thumbnail || "",
      });
      toast.success("Course details saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const deleteCourse = async () => {
    if (!window.confirm(`Delete the entire course "${course.title}"? All lessons, quizzes and learner progress will be removed.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/courses/${slug}`);
      toast.success("Course deleted");
      onClose(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const saveLesson = async () => {
    if (!editingLesson?.title?.trim() || !editingLesson?.content?.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setBusy(true);
    try {
      if (editingLesson.order) {
        await api.put(`/admin/courses/${slug}/lessons/${editingLesson.order}`, {
          title: editingLesson.title,
          summary: editingLesson.summary || "",
          content: editingLesson.content,
        });
        toast.success("Lesson updated");
      } else {
        await api.post(`/admin/courses/${slug}/lessons`, {
          title: editingLesson.title,
          summary: editingLesson.summary || "",
          content: editingLesson.content,
        });
        toast.success("Lesson added");
      }
      setEditingLesson(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const deleteLesson = async (order, title) => {
    if (!window.confirm(`Delete lesson "${title}"?`)) return;
    try {
      await api.delete(`/admin/courses/${slug}/lessons/${order}`);
      toast.success("Lesson deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const startNewQuiz = () => {
    setQuizDraft(course.quiz && course.quiz.questions ? JSON.parse(JSON.stringify(course.quiz)) : { title: `${course.title} — Quiz`, pass_score: 80, questions: [] });
  };

  const saveQuiz = async () => {
    if (!quizDraft.questions.length) {
      toast.error("Add at least one question");
      return;
    }
    for (const q of quizDraft.questions) {
      if (!q.question.trim() || q.options.filter((o) => o.trim()).length < 2) {
        toast.error("Every question needs a title and at least 2 options");
        return;
      }
      if (q.answer_index < 0 || q.answer_index >= q.options.length) {
        toast.error("Select a correct answer for every question");
        return;
      }
    }
    setBusy(true);
    try {
      await api.put(`/admin/courses/${slug}/quiz`, quizDraft);
      toast.success("Quiz saved");
      setQuizDraft(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const startFlashcards = () => {
    setCardsDraft(course.flashcards ? JSON.parse(JSON.stringify(course.flashcards)) : []);
  };
  const saveFlashcards = async () => {
    for (const c of cardsDraft) {
      if (!c.front.trim() || !c.back.trim()) {
        toast.error("Every card needs a front and a back");
        return;
      }
    }
    setBusy(true);
    try {
      await api.put(`/admin/courses/${slug}/flashcards`, { flashcards: cardsDraft });
      toast.success("Flashcards saved");
      setCardsDraft(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const tabs = [
    { id: "meta", label: "Details" },
    { id: "lessons", label: `Lessons (${(course.lessons || []).length})` },
    { id: "quiz", label: `Quiz (${((course.quiz && course.quiz.questions) || []).length})` },
    { id: "flashcards", label: `Flashcards (${(course.flashcards || []).length})` },
  ];

  return (
    <div className="mt-8 bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden" data-testid={TESTIDS.courseEditor}>
      <div className="p-6 border-b border-brand-linen flex flex-wrap items-center gap-3">
        <button
          data-testid={TESTIDS.courseEditorBack}
          onClick={() => onClose(false)}
          className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-3 py-1.5 text-xs hover:bg-brand-linen"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={2} /> All courses
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-brand-muted">Editing course</div>
          <h2 className="font-serif text-2xl text-brand-ink truncate">{course.title}</h2>
        </div>
        <button
          data-testid={TESTIDS.courseDelete}
          onClick={deleteCourse}
          disabled={busy}
          className="inline-flex items-center gap-1.5 border border-brand-accent text-brand-accent rounded-full px-4 py-2 text-sm hover:bg-brand-accent hover:text-white"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} /> Delete course
        </button>
      </div>

      <div className="px-6 border-b border-brand-linen flex gap-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`course-editor-tab-${t.id}`}
            className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-brand-ink text-brand-ink font-medium" : "border-transparent text-brand-muted hover:text-brand-ink"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "meta" && (
        <div className="p-6 grid md:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brand-muted">Title</span>
            <input value={course.title || ""} onChange={(e) => setCourse({ ...course, title: e.target.value })}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brand-muted">Level</span>
            <input value={course.level || ""} onChange={(e) => setCourse({ ...course, level: e.target.value })}
              placeholder="Foundation" className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-widest text-brand-muted">Subtitle</span>
            <input value={course.subtitle || ""} onChange={(e) => setCourse({ ...course, subtitle: e.target.value })}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-widest text-brand-muted">Description</span>
            <textarea value={course.description || ""} onChange={(e) => setCourse({ ...course, description: e.target.value })}
              rows={4} className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brand-muted">Duration</span>
            <input value={course.duration || ""} onChange={(e) => setCourse({ ...course, duration: e.target.value })}
              placeholder="e.g. 30 minutes" className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brand-muted">Thumbnail URL (optional)</span>
            <input value={course.thumbnail || ""} onChange={(e) => setCourse({ ...course, thumbnail: e.target.value })}
              placeholder="https://…" className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button data-testid={TESTIDS.courseSaveMeta} onClick={saveMeta} disabled={busy}
              className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover disabled:opacity-60">
              <Save className="w-4 h-4" strokeWidth={1.5} /> {busy ? "Saving…" : "Save details"}
            </button>
          </div>
        </div>
      )}

      {tab === "lessons" && (
        <div className="p-6 space-y-4">
          {(course.lessons || []).map((l) => (
            <div key={l.order} className="border border-brand-linen rounded-2xl p-4 bg-brand-bone/40 flex items-start gap-4">
              <div className="text-[11px] uppercase tracking-widest text-brand-muted font-mono w-8 pt-1">#{l.order}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-brand-ink">{l.title}</div>
                {l.summary && <div className="text-sm text-brand-muted mt-1">{l.summary}</div>}
              </div>
              <button
                data-testid={TESTIDS.lessonEdit}
                onClick={() => setEditingLesson({ ...l })}
                className="p-2 border border-brand-linen rounded-full hover:bg-white"
                title="Edit lesson"
              >
                <Pencil className="w-3.5 h-3.5 text-brand-ink" strokeWidth={1.7} />
              </button>
              <button
                data-testid={TESTIDS.lessonDelete}
                onClick={() => deleteLesson(l.order, l.title)}
                className="p-2 border border-brand-linen rounded-full hover:bg-brand-accent hover:text-white hover:border-brand-accent"
                title="Delete lesson"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} />
              </button>
            </div>
          ))}
          {(!course.lessons || course.lessons.length === 0) && (
            <div className="text-brand-muted text-sm italic border border-dashed border-brand-linen rounded-2xl p-6 text-center">
              No lessons yet. Add your first one below.
            </div>
          )}
          <button data-testid={TESTIDS.lessonNew} onClick={() => setEditingLesson({ title: "", summary: "", content: "" })}
            className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover text-sm">
            <Plus className="w-4 h-4" strokeWidth={1.7} /> Add lesson
          </button>

          {editingLesson && (
            <div className="mt-6 bg-white border border-brand-linen rounded-2xl p-5 shadow-card space-y-4">
              <div className="text-xs uppercase tracking-widest text-brand-muted">
                {editingLesson.order ? `Editing lesson #${editingLesson.order}` : "New lesson"}
              </div>
              <input value={editingLesson.title} onChange={(e) => setEditingLesson({ ...editingLesson, title: e.target.value })}
                placeholder="Lesson title"
                className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
              <input value={editingLesson.summary || ""} onChange={(e) => setEditingLesson({ ...editingLesson, summary: e.target.value })}
                placeholder="One-line summary (shown in the lesson list)"
                className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
              <textarea value={editingLesson.content} onChange={(e) => setEditingLesson({ ...editingLesson, content: e.target.value })}
                placeholder="Lesson body. Markdown-lite supported: **bold**, *italic*, • bullets, blank lines for paragraphs."
                rows={14}
                className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink font-mono text-sm" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditingLesson(null)}
                  className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen">
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button data-testid={TESTIDS.lessonSave} onClick={saveLesson} disabled={busy}
                  className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-bone rounded-full px-5 py-2 text-sm hover:bg-brand-inkHover disabled:opacity-60">
                  <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save lesson"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "quiz" && (
        <div className="p-6 space-y-4">
          {!quizDraft && (
            <>
              {((course.quiz && course.quiz.questions) || []).map((q, idx) => (
                <div key={idx} className="border border-brand-linen rounded-2xl p-4 bg-brand-bone/40">
                  <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Q{idx + 1}</div>
                  <div className="font-medium text-brand-ink">{q.question}</div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {q.options.map((o, i) => (
                      <li key={i} className={`flex items-center gap-2 ${i === q.answer_index ? "text-brand-secondary font-medium" : "text-brand-body"}`}>
                        {i === q.answer_index ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : <span className="w-3.5 h-3.5 inline-block" />}
                        {o}
                      </li>
                    ))}
                  </ul>
                  {q.explanation && <div className="text-xs text-brand-muted mt-2 italic">{q.explanation}</div>}
                </div>
              ))}
              {(!course.quiz || !course.quiz.questions?.length) && (
                <div className="text-brand-muted text-sm italic border border-dashed border-brand-linen rounded-2xl p-6 text-center">
                  No quiz yet.
                </div>
              )}
              <button onClick={startNewQuiz}
                className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover text-sm">
                <Pencil className="w-4 h-4" strokeWidth={1.7} /> Edit quiz
              </button>
            </>
          )}

          {quizDraft && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-brand-muted">Quiz title</span>
                  <input value={quizDraft.title} onChange={(e) => setQuizDraft({ ...quizDraft, title: e.target.value })}
                    className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-brand-muted">Pass score %</span>
                  <input type="number" min="1" max="100" value={quizDraft.pass_score}
                    onChange={(e) => setQuizDraft({ ...quizDraft, pass_score: parseInt(e.target.value || "80", 10) })}
                    className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
                </label>
              </div>

              {quizDraft.questions.map((q, qi) => (
                <div key={qi} className="border border-brand-linen rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="text-xs uppercase tracking-widest text-brand-muted flex-1">Q{qi + 1}</div>
                    <button onClick={() => setQuizDraft({ ...quizDraft, questions: quizDraft.questions.filter((_, i) => i !== qi) })}
                      className="p-1.5 border border-brand-linen rounded-full hover:bg-brand-accent hover:text-white hover:border-brand-accent" title="Remove">
                      <Trash2 className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                  <input value={q.question} onChange={(e) => {
                    const qs = [...quizDraft.questions]; qs[qi] = { ...q, question: e.target.value }; setQuizDraft({ ...quizDraft, questions: qs });
                  }} placeholder="Question text"
                    className="w-full px-3 py-2 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink" />
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <button type="button"
                          onClick={() => { const qs = [...quizDraft.questions]; qs[qi] = { ...q, answer_index: oi }; setQuizDraft({ ...quizDraft, questions: qs }); }}
                          className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs ${oi === q.answer_index ? "bg-brand-ink text-brand-bone border-brand-ink" : "border-brand-linen"}`}
                          title="Correct answer">
                          {oi === q.answer_index ? <Check className="w-3 h-3" strokeWidth={3} /> : ""}
                        </button>
                        <input value={opt} onChange={(e) => {
                          const qs = [...quizDraft.questions]; const opts = [...q.options]; opts[oi] = e.target.value; qs[qi] = { ...q, options: opts }; setQuizDraft({ ...quizDraft, questions: qs });
                        }} placeholder={`Option ${oi + 1}`}
                          className="flex-1 px-3 py-2 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink text-sm" />
                        {q.options.length > 2 && (
                          <button onClick={() => {
                            const qs = [...quizDraft.questions]; const opts = q.options.filter((_, i) => i !== oi);
                            const newIdx = q.answer_index >= opts.length ? 0 : (q.answer_index === oi ? 0 : (q.answer_index > oi ? q.answer_index - 1 : q.answer_index));
                            qs[qi] = { ...q, options: opts, answer_index: newIdx }; setQuizDraft({ ...quizDraft, questions: qs });
                          }} className="p-1.5 border border-brand-linen rounded-full hover:bg-brand-linen" title="Remove option">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    {q.options.length < 6 && (
                      <button onClick={() => {
                        const qs = [...quizDraft.questions]; qs[qi] = { ...q, options: [...q.options, ""] }; setQuizDraft({ ...quizDraft, questions: qs });
                      }} className="text-xs text-brand-muted hover:text-brand-ink">+ add option</button>
                    )}
                  </div>
                  <input value={q.explanation || ""} onChange={(e) => {
                    const qs = [...quizDraft.questions]; qs[qi] = { ...q, explanation: e.target.value }; setQuizDraft({ ...quizDraft, questions: qs });
                  }} placeholder="Explanation shown after they answer (optional)"
                    className="w-full px-3 py-2 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink text-sm" />
                </div>
              ))}

              <div className="flex gap-2 justify-between">
                <button data-testid={TESTIDS.quizAddQuestion} onClick={() => setQuizDraft({ ...quizDraft, questions: [...quizDraft.questions, { question: "", options: ["", ""], answer_index: 0, explanation: "" }] })}
                  className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen">
                  <Plus className="w-3.5 h-3.5" /> Add question
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setQuizDraft(null)}
                    className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen">
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                  <button data-testid={TESTIDS.quizSaveAll} onClick={saveQuiz} disabled={busy}
                    className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-bone rounded-full px-5 py-2 text-sm hover:bg-brand-inkHover disabled:opacity-60">
                    <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save quiz"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "flashcards" && (
        <div className="p-6 space-y-4">
          {!cardsDraft && (
            <>
              {(course.flashcards || []).map((c, i) => (
                <div key={i} className="border border-brand-linen rounded-2xl p-4 bg-brand-bone/40 grid md:grid-cols-2 gap-4">
                  <div><div className="text-[11px] uppercase tracking-widest text-brand-muted mb-1">Front</div><div className="text-sm">{c.front}</div></div>
                  <div><div className="text-[11px] uppercase tracking-widest text-brand-muted mb-1">Back</div><div className="text-sm">{c.back}</div></div>
                </div>
              ))}
              {(!course.flashcards || !course.flashcards.length) && (
                <div className="text-brand-muted text-sm italic border border-dashed border-brand-linen rounded-2xl p-6 text-center">
                  No flashcards yet.
                </div>
              )}
              <button onClick={startFlashcards}
                className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover text-sm">
                <Pencil className="w-4 h-4" strokeWidth={1.7} /> Edit flashcards
              </button>
            </>
          )}

          {cardsDraft && (
            <div className="space-y-3">
              {cardsDraft.map((c, i) => (
                <div key={i} className="border border-brand-linen rounded-2xl p-4 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
                  <textarea value={c.front} rows={3} onChange={(e) => { const arr = [...cardsDraft]; arr[i] = { ...c, front: e.target.value }; setCardsDraft(arr); }}
                    placeholder="Front" className="px-3 py-2 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink text-sm" />
                  <textarea value={c.back} rows={3} onChange={(e) => { const arr = [...cardsDraft]; arr[i] = { ...c, back: e.target.value }; setCardsDraft(arr); }}
                    placeholder="Back" className="px-3 py-2 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink text-sm" />
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setCardsDraft(cardsDraft.filter((_, k) => k !== i))}
                      className="p-1.5 border border-brand-linen rounded-full hover:bg-brand-accent hover:text-white hover:border-brand-accent">
                      <Trash2 className="w-3 h-3" strokeWidth={2} />
                    </button>
                    {i > 0 && <button onClick={() => { const arr = [...cardsDraft]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setCardsDraft(arr); }}
                      className="p-1.5 border border-brand-linen rounded-full hover:bg-brand-linen"><ChevronUp className="w-3 h-3" strokeWidth={2} /></button>}
                    {i < cardsDraft.length - 1 && <button onClick={() => { const arr = [...cardsDraft]; [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; setCardsDraft(arr); }}
                      className="p-1.5 border border-brand-linen rounded-full hover:bg-brand-linen"><ChevronDown className="w-3 h-3" strokeWidth={2} /></button>}
                  </div>
                </div>
              ))}
              <div className="flex gap-2 justify-between">
                <button data-testid={TESTIDS.flashcardAdd} onClick={() => setCardsDraft([...cardsDraft, { front: "", back: "" }])}
                  className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen">
                  <Plus className="w-3.5 h-3.5" /> Add card
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setCardsDraft(null)}
                    className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen">
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                  <button data-testid={TESTIDS.flashcardSaveAll} onClick={saveFlashcards} disabled={busy}
                    className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-bone rounded-full px-5 py-2 text-sm hover:bg-brand-inkHover disabled:opacity-60">
                    <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save flashcards"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Course manager: list + create modal, hosted inside Admin.jsx
// -----------------------------------------------------------------------------
// TagChips: display + edit pill tags for a course.
// Fetches allowed tags once. Practice admins can edit their own practice's
// courses; superadmins can edit any course (community or practice).
// -----------------------------------------------------------------------------
function TagChips({ course, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [allowed, setAllowed] = useState([]);
  const [selected, setSelected] = useState(course.tags || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => setSelected(course.tags || []), [course.tags]);

  const openEditor = async () => {
    if (allowed.length === 0) {
      try {
        const { data } = await api.get("/courses/tags/allowed");
        setAllowed(data.tags || []);
      } catch { toast.error("Could not load tag list"); return; }
    }
    setEditing(true);
  };

  const toggle = (t) => {
    setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/courses/${course.slug}/tags`, { tags: selected });
      toast.success("Tags updated");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not save tags");
    } finally {
      setSaving(false);
    }
  };

  const displayTags = course.tags && course.tags.length ? course.tags : [];

  return (
    <div className="mt-1.5" data-testid={`course-tag-chips-${course.slug}`}>
      <div className="flex flex-wrap gap-1 items-center">
        {displayTags.map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-brand-linen text-brand-ink/80">
            {t}
          </span>
        ))}
        {displayTags.length === 0 && (
          <span className="text-[10px] uppercase tracking-widest text-brand-muted italic">
            No custom tags {canEdit ? "— using default" : ""}
          </span>
        )}
        {canEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); openEditor(); }}
            className="ml-1 text-[10px] uppercase tracking-widest text-brand-accent hover:underline"
            data-testid={`edit-tags-${course.slug}`}
          >
            Edit tags
          </button>
        )}
      </div>
      {editing && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="mt-3 p-4 rounded-xl border border-brand-linen bg-brand-bone/40"
          data-testid={`tag-editor-${course.slug}`}
        >
          <div className="text-xs uppercase tracking-widest text-brand-muted mb-3">
            Choose tags — pick any that apply
          </div>
          <div className="flex flex-wrap gap-2">
            {allowed.map((t) => {
              const on = selected.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    on
                      ? "bg-brand-ink text-brand-bone border-brand-ink"
                      : "bg-white text-brand-ink border-brand-linen hover:border-brand-secondary"
                  }`}
                  data-testid={`tag-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1 bg-brand-ink text-brand-bone rounded-full px-4 py-1.5 text-xs disabled:opacity-60"
              data-testid={`save-tags-${course.slug}`}
            >
              <Save className="w-3 h-3" strokeWidth={2} /> Save tags
            </button>
            <button
              onClick={() => { setSelected(course.tags || []); setEditing(false); }}
              className="text-xs text-brand-muted hover:text-brand-ink"
              data-testid={`cancel-tags-${course.slug}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
export function CourseManager({ practiceName }) {
  const [courses, setCourses] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [creating, setCreating] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [draft, setDraft] = useState({ title: "", subtitle: "", description: "", level: "Foundation", duration: "" });
  const [busy, setBusy] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const loadCourses = async () => {
    try {
      const { data } = await api.get("/courses");
      setCourses(data);
    } catch { /* silent */ }
  };
  useEffect(() => {
    loadCourses();
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setIsSuperAdmin(!!data.is_super_admin);
      } catch { /* silent */ }
    })();
  }, []);

  const { practiceCourses, community } = useMemo(() => {
    const p = [], c = [];
    for (const cc of courses) {
      if (cc.practice_id) p.push(cc);
      else c.push(cc);
    }
    return { practiceCourses: p, community: c };
  }, [courses]);

  const submitNew = async () => {
    if (!draft.title.trim() || !draft.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/courses", {
        slug: draft.title,
        title: draft.title.trim(),
        subtitle: draft.subtitle.trim(),
        description: draft.description.trim(),
        level: draft.level.trim() || "Foundation",
        duration: draft.duration.trim(),
      });
      toast.success("Course created — now add lessons and a quiz");
      setCreating(false);
      setDraft({ title: "", subtitle: "", description: "", level: "Foundation", duration: "" });
      await loadCourses();
      setEditingSlug(data.slug);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  if (editingSlug) {
    return <CourseEditor slug={editingSlug} onClose={async (deleted) => { setEditingSlug(null); if (deleted) await loadCourses(); else await loadCourses(); }} />;
  }

  return (
    <div className="mt-12 bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden">
      <div className="p-6 border-b border-brand-linen flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-2xl text-brand-ink">Courses</h2>
          <p className="text-sm text-brand-muted mt-1">
            Author your own practice-specific courses (safeguarding refresher, local induction, chaperoning SOP…). The Calside library is shown below — read-only, available to every practice.
          </p>
        </div>
        <button data-testid={TESTIDS.courseHelpBtn} onClick={() => setHelpOpen(true)}
          className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-4 py-2.5 hover:bg-brand-linen text-sm"
          title="How to add courses">
          <HelpCircle className="w-4 h-4" strokeWidth={1.7} /> How does this work?
        </button>
        <button data-testid={TESTIDS.courseNewBtn} onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover">
          <Plus className="w-4 h-4" strokeWidth={1.7} /> New course
        </button>
      </div>
      <CourseManagerHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      {creating && (
        <div className="p-6 border-b border-brand-linen bg-brand-bone/40 space-y-3">
          <div className="text-xs uppercase tracking-widest text-brand-muted">New course</div>
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Course title (e.g. team induction pack)"
            className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-white focus:outline-none focus:border-brand-ink" />
          <input value={draft.subtitle} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
            placeholder="Short one-line subtitle (optional)"
            className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-white focus:outline-none focus:border-brand-ink" />
          <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="Longer description (shown on the course card)" rows={3}
            className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-white focus:outline-none focus:border-brand-ink" />
          <div className="grid grid-cols-2 gap-3">
            <input value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value })}
              placeholder="Level (e.g. Foundation)"
              className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-white focus:outline-none focus:border-brand-ink" />
            <input value={draft.duration} onChange={(e) => setDraft({ ...draft, duration: e.target.value })}
              placeholder="Duration (e.g. 30 minutes)"
              className="w-full px-4 py-3 border border-brand-linen rounded-xl bg-white focus:outline-none focus:border-brand-ink" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreating(false)}
              className="inline-flex items-center gap-1.5 border border-brand-linen text-brand-ink rounded-full px-4 py-2 text-sm hover:bg-brand-linen">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button data-testid={TESTIDS.courseCreateSubmit} onClick={submitNew} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-bone rounded-full px-5 py-2 text-sm hover:bg-brand-inkHover disabled:opacity-60">
              <Save className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create & edit"}
            </button>
          </div>
        </div>
      )}

      <div className="p-6">
        <div className="text-[11px] uppercase tracking-widest text-brand-muted mb-3">
          Your practice&apos;s courses ({practiceCourses.length})
          {practiceName ? ` · ${practiceName}` : ""}
        </div>
        {practiceCourses.length === 0 && (
          <div className="text-sm text-brand-muted italic border border-dashed border-brand-linen rounded-2xl p-6 text-center">
            No practice-specific courses yet. Click <em>New course</em> to add your local induction, chaperoning SOP or fire safety refresher.
          </div>
        )}
        <div className="space-y-2">
          {practiceCourses.map((c) => (
            <div key={c.slug} className="border border-brand-linen rounded-2xl p-4 hover:bg-brand-bone/60 transition-colors">
              <button
                data-testid={TESTIDS.courseListItem}
                onClick={() => setEditingSlug(c.slug)}
                className="w-full text-left flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-ink">{c.title}</div>
                  <div className="text-xs text-brand-muted mt-0.5">
                    {c.level}{c.duration ? ` · ${c.duration}` : ""} · {c.subtitle || c.description}
                  </div>
                </div>
                <Pencil className="w-4 h-4 text-brand-muted" strokeWidth={1.7} />
              </button>
              <TagChips course={c} canEdit={true} onSaved={loadCourses} />
            </div>
          ))}
        </div>

        <div className="text-[11px] uppercase tracking-widest text-brand-muted mt-8 mb-3">
          Calside library ({community.length}) {isSuperAdmin ? "· superadmin tag editing" : "· read-only"}
        </div>
        <div className="space-y-2">
          {community.map((c) => (
            <div key={c.slug} className="border border-brand-linen rounded-2xl p-4 bg-brand-bone/30">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-ink">{c.title}</div>
                  <div className="text-xs text-brand-muted mt-0.5">{c.subtitle || c.description}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-brand-muted">Community</span>
              </div>
              <TagChips course={c} canEdit={isSuperAdmin} onSaved={loadCourses} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
